import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';

import toast from 'components/partials/toast/toast';

import { useCopyAidModel } from '../../hooks/use-aid-models';
import { useUpdateAidModel } from './use-aid-model-details';
import { regimeKeys, regimeGroupKeys } from './use-aid-regimes';

import { handleAidModelError } from '../utils/aid-model-error-handler';

import { MODEL_SAVED_TOAST_TITLE } from '../aid-studio-details.constants';

import type { CopyModelValues } from '../components/model-modals';
import type { AidStudioFormValues } from './use-aid-studio-form';
import type { AidModelUpdateRequestEnrollmentType } from 'api/generated/schemas/aidModelUpdateRequestEnrollmentType';

export type CopyModelMode = 'copyToEdit' | 'saveAsNewVersion';

interface UseModelCopyParams {
  userId: string;
  clientId: number;
  sourceModelId: string | number | undefined;
  landingPath: (newModelId: number) => string;
  hasUnsavedChanges: boolean;
  getFormValues?: () => AidStudioFormValues;
}

interface UseModelCopyResult {
  mode: CopyModelMode | null;
  openCopyToEdit: () => void;
  openSaveAsNewVersion: () => void;
  closeModal: () => void;
  handleCopy: (values: CopyModelValues) => Promise<void>;
}

const MODAL_HEADERS: Record<CopyModelMode, string> = {
  copyToEdit: 'Clone to edit',
  saveAsNewVersion: 'Clone',
};

export const getCopyModalHeader = (mode: CopyModelMode): string =>
  MODAL_HEADERS[mode];

export const useModelCopy = ({
  userId,
  clientId,
  sourceModelId,
  landingPath,
  hasUnsavedChanges,
  getFormValues,
}: UseModelCopyParams): UseModelCopyResult => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [mode, setMode] = useState<CopyModelMode | null>(null);

  const copyModel = useCopyAidModel({ userId, clientId });
  const updateModel = useUpdateAidModel();

  const closeModal = useCallback(() => setMode(null), []);

  const openCopyToEdit = useCallback(() => setMode('copyToEdit'), []);

  const openSaveAsNewVersion = useCallback(
    () => setMode('saveAsNewVersion'),
    []
  );

  const handleCopy = useCallback(
    async ({ name, description }: CopyModelValues): Promise<void> => {
      if (sourceModelId == null) return;

      try {
        const newModel = await copyModel.mutateAsync({
          sourceModelId,
          data: { name, description: description || undefined },
        });

        if (newModel.id == null) return;

        const values = getFormValues?.();

        if (mode === 'saveAsNewVersion' && hasUnsavedChanges && values) {
          await updateModel.mutateAsync({
            userId,
            clientId,
            modelId: newModel.id,
            data: {
              name,
              description: description || values.description || null,
              entryTerm: values.entryTerm,
              enrollmentType:
                values.enrollmentType as AidModelUpdateRequestEnrollmentType,
            },
          });
        }

        toast.success({
          title: MODEL_SAVED_TOAST_TITLE,
          message: `You can find ${name} in the list of models`,
        });
        closeModal();

        queryClient.removeQueries({
          queryKey: regimeKeys.model(String(newModel.id)),
        });
        queryClient.removeQueries({
          queryKey: regimeGroupKeys.model(String(newModel.id)),
        });

        navigate(landingPath(newModel.id), { replace: true });
      } catch (error) {
        handleAidModelError(error, 'Failed to save model');
      }
    },
    [
      sourceModelId,
      copyModel,
      mode,
      hasUnsavedChanges,
      getFormValues,
      updateModel,
      userId,
      clientId,
      closeModal,
      queryClient,
      navigate,
      landingPath,
    ]
  );

  return {
    mode,
    openCopyToEdit,
    openSaveAsNewVersion,
    closeModal,
    handleCopy,
  };
};
